import React from 'react';
import { DataTable } from '../../components/ui/DataTable';
import { SectionHeader } from '../../components/ui/SectionHeader';
import { StatusPill } from '../../components/ui/StatusPill';
import { getSystemLogs, SystemLog } from '../../services/system';

const PAGE_SIZE = 50;

export function AuditLog({ searchQuery = '' }: { searchQuery?: string }) {
  const [logs, setLogs] = React.useState<SystemLog[]>([]);
  const [total, setTotal] = React.useState(0);
  const [offset, setOffset] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState(searchQuery.trim());

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setOffset(0);
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchQuery]);

  React.useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setError('');

    getSystemLogs(PAGE_SIZE, offset, debouncedSearch)
      .then((response) => {
        if (isActive) {
          setLogs(response.logs);
          setTotal(response.total);
        }
      })
      .catch((requestError) => {
        if (isActive) {
          setLogs([]);
          setError(requestError instanceof Error ? requestError.message : 'Unable to load system logs');
        }
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [debouncedSearch, offset]);

  const pageStart = total ? offset + 1 : 0;
  const pageEnd = Math.min(offset + logs.length, total);

  return (
    <div className="audit-log-page">
      <SectionHeader
        actions={<StatusPill tone="info">{logs.length} shown</StatusPill>}
        className="section-head audit-log-head"
        subtitle={`${total.toLocaleString()} recorded event${total === 1 ? '' : 's'}`}
        title="System logs"
      />

      {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}

      <DataTable className="audit-log-table">
          <thead>
            <tr>
              <th>Actor</th>
              <th>Action</th>
              <th>Target</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td className="kyc-table-state" colSpan={4}>Loading system logs...</td></tr>
            ) : logs.length ? logs.map((log) => (
              <tr key={log.id}>
                <td data-label="Actor"><strong>{log.actor}</strong></td>
                <td data-label="Action"><span className="audit-action">{log.action}</span></td>
                <td data-label="Target"><span className="audit-target">{log.target}</span></td>
                <td data-label="Time">{formatLogTime(log.time)}</td>
              </tr>
            )) : (
              <tr>
                <td className="kyc-table-state" colSpan={4}>
                  {debouncedSearch ? `No system logs match "${debouncedSearch}".` : 'No system logs found.'}
                </td>
              </tr>
            )}
          </tbody>
      </DataTable>

      <div className="passengers-pagination">
        <span>Showing {pageStart}-{pageEnd} of {total.toLocaleString()}</span>
        <div>
          <button className="btn" disabled={offset === 0 || isLoading} type="button" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
          <button className="btn" disabled={offset + PAGE_SIZE >= total || isLoading} type="button" onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
        </div>
      </div>
    </div>
  );
}

function formatLogTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
